import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map, tap } from 'rxjs/operators';

import { ApiService } from '@api/core';
import {
  LoginRequest,
  LoginResponse,
  Modules,
  RenewalAmountDTO,
  RenewalOfferDTO,
  RenewalPaymentRequestDTO,
  RenewalPaymentResponseDTO,
  UserDetails,
  UserRegister,
} from './user.model';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Patient } from '../patient';
import { environment } from '@environment/environment';

const API_URL = 'user';
const ZOOM_RECOMMENDATION_NOTICE_PENDING_KEY = 'zoom_recommendation_notice_pending';
const ZOOM_RECOMMENDATION_NOTICE_SHOWN_KEY = 'zoom_recommendation_notice_shown';

@Injectable()
export class UserApiService {
  auth: LoginResponse = null;
  suscription: boolean = true

  get institutionId(): string {
    return '' + this.auth.userDetails.id_account as string;
  }

  get matricula(): string {
    return this.auth.userDetails.licence;
  }

  constructor(private api: ApiService, private http: HttpClient) { }

  private getUrl(api: string, path: string = ''): string {
    return `${environment.api[api]}${path}`;
  }

  private getTimezoneHeader(): HttpHeaders {
    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const login_response: LoginResponse = JSON.parse(sessionStorage.getItem('login_response'));
    return new HttpHeaders({
      'X-Timezone': timeZone,
      'X-Account-Id': login_response.userDetails.id_account.toString(),
      'X-User-Email': login_response.userDetails.username,
      'X-Professional-Id': login_response.userDetails.id_person.toString(),
      'Content-Type': 'application/json',
      Authorization: `Basic ${this.api.getBasicAuth()}`
    });
  }

  private getTimezoneHeaderPublic(): HttpHeaders {
    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const login_response: LoginResponse = JSON.parse(sessionStorage.getItem('login_response'));
    return new HttpHeaders({
      'X-Timezone': timeZone,
      'Content-Type': 'application/json',
    });
  }

  forgotPassword(email: string, id_account: number) {
    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        Authorization: `Basic ${this.api.getBasicAuth()}`,
      }),
    };
    const service = this.api.post<any>(
      'registerUser',
      '/forgot-password',
      { username: email, license: id_account },
      httpOptions,
    );
    service.subscribe(this.setUser);
    return service;
  }

  registerUser(
    data
  ): Observable<any> {

    const {
      name,
      lastName,
      email,
      tel,
      institution,
      birthDate,
      areaCode,
      document } = data;
    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        Authorization: `Basic ${this.api.getBasicAuth()}`
      })
    };
    const model: UserRegister = {
      birthday: birthDate,
      documentType: {
        code: "DNI",
        description: "DNI"
      },
      email,
      gender: {
        "code": "M",
        "description": "MASCULINO"
      },
      name,
      institution,
      numberDocument: Number(document),
      surname: lastName,
      /**
       *    "telephones":[
     {
        "type":{
           "id":1,
           "description":"Celular"
        },
        "areaCode":11,
        "number":68770982
     }
  ]
       */
      telephones: [
        {
          "type": {
            "id": 1,
            "description": "Celular"
          },
          "areaCode": Number(areaCode),
          "number": Number(tel)
        }


      ],
    }
    return this.api.post<any>(
      'registerUser',
      '/register?accountId=' + institution,
      model,
      httpOptions,
    );

  }

  changePassword(password: string, oldPassword: string) {
    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        Authorization: `Basic ${this.api.getBasicAuth()}`,
      }),
    };
    const service = this.api.post<any>(
      'registerUser',
      '/change-password',
      { password, oldPassword, id: this.auth.userDetails.id },
      httpOptions,
    );
    service.subscribe(this.setUser);
    return service;
  }

  resetPassword(username: string, password: string, id: number, accountId: number) {
    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        Authorization: `Basic ${this.api.getBasicAuth()}`,
      }),
    };
    const service = this.api.post<any>(
      'registerUser',
      '/reset-password',
      { username, password, id: id, accountId },
      httpOptions,
    );
    return service;
  }

  getInstitutions(): Observable<any> {
    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        Authorization: `Basic ${this.api.getBasicAuth()}`,
      }),
    };
    const service = this.api.get<any>(
      'registerUser',
      'domain/accounts'
    );
    service.subscribe(this.setUser);
    return service;
  }

  doLoginRequest(userData: LoginRequest): Observable<any> {
    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        Authorization: `Basic ${this.api.getBasicAuth()}`,
      }),
    };
    const service = this.api.post<LoginResponse>(
      'security',
      '/oauth/token',
      userData,
      httpOptions,
    ).pipe(
      map((response: LoginResponse) => ({
        ...response,
        userDetails: {
          ...response.userDetails,
          licence: userData.username,
        },
      } as LoginResponse)),
    );
    service.subscribe((response) => {
      this.setUser(response);
      this.queueZoomRecommendationNotice();
    });
    return service;
  }

  getUser(): UserDetails {
    return this.auth.userDetails;
  }

  setUserName(patient: Patient): void {
    this.auth.userDetails.name = patient.name;
    this.auth.userDetails.lastName = patient.surname;
  }

  getLoggedUser(): LoginResponse {
    const loginAuth = sessionStorage.getItem("login_response");
    if (loginAuth)
      return JSON.parse(loginAuth) as LoginResponse;

  }

  setUser = (response: LoginResponse) => {
    this.auth = response;
    this.auth.userDetails.instituteId = '' + response.userDetails.id_account;
    this.auth.userDetails.roles = response.roleDefinitions.map(r => r.rolename).join(",");
    sessionStorage.setItem('login_response', JSON.stringify(response));
  }

  queueZoomRecommendationNotice(): void {
    sessionStorage.setItem(ZOOM_RECOMMENDATION_NOTICE_PENDING_KEY, 'true');
    sessionStorage.removeItem(ZOOM_RECOMMENDATION_NOTICE_SHOWN_KEY);
  }

  shouldShowZoomRecommendationNotice(): boolean {
    const isPending = sessionStorage.getItem(ZOOM_RECOMMENDATION_NOTICE_PENDING_KEY) === 'true';
    const wasShown = sessionStorage.getItem(ZOOM_RECOMMENDATION_NOTICE_SHOWN_KEY) === 'true';

    if (!isPending || wasShown) {
      return false;
    }

    sessionStorage.removeItem(ZOOM_RECOMMENDATION_NOTICE_PENDING_KEY);
    sessionStorage.setItem(ZOOM_RECOMMENDATION_NOTICE_SHOWN_KEY, 'true');
    return true;
  }

  acceptLegalTerms(): Observable<LoginResponse> {
    const loggedUser = this.getLoggedUser();

    return this.api.post<any>('security', '/users/me/tc-accept', {
      licence: loggedUser?.userDetails?.licence,
    }).pipe(
      map(() => {
        return {
          ...loggedUser,
          userDetails: {
            ...loggedUser.userDetails,
            tc: true,
          },
        } as LoginResponse;
      }),
      tap(this.setUser),
    );
  }

  logOut() {
    sessionStorage.removeItem('login_response');
    sessionStorage.removeItem(ZOOM_RECOMMENDATION_NOTICE_PENDING_KEY);
    sessionStorage.removeItem(ZOOM_RECOMMENDATION_NOTICE_SHOWN_KEY);
    this.auth = null;
  }

  refreshToken(): Observable<any> {
    const req = {
      grant_type: "refresh_token",
      refresh_token: this.getLoggedUser().refresh_token,
    }

    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        Authorization: `Basic ${this.api.getBasicAuth()}`,
      }),
    };
    const service = this.api.post<any>(
      'security',
      '/oauth/token',
      req,
      httpOptions,
    );
    service.subscribe(this.setUser);
    return service;
  }

  public canAccessToModule(moduleName: AuthModules | string, formatModuleName = (o) => o) {
    let module = this.getAuthModule(moduleName, formatModuleName);
    if (module && (module.read || module.create || module.update || module.delete))
      return true;
    return false;
  }

  public canAccessRenewal(): boolean {
    const roles = this.getCurrentRoles();
    return roles.includes('ROLE_ROOT') || roles.includes('ROLE_SECRETARY');
  }

  private getCurrentRoles(): string[] {
    const authRoles = String(this.auth?.userDetails?.roles || '').split(',');
    if (authRoles.some((role) => role.trim())) {
      return authRoles.map((role) => role.trim()).filter(Boolean);
    }

    const loginAuth = this.getLoggedUser();
    const sessionRoles = String(loginAuth?.userDetails?.roles || '').split(',');
    return sessionRoles.map((role) => role.trim()).filter(Boolean);
  }

  getRenewalAmount(suscriptionId: number, licenceId: number): Observable<RenewalAmountDTO> {
    const params = new HttpParams()
      .set('suscriptionId', String(suscriptionId))
      .set('licenceId', String(licenceId));

    return this.api.get<RenewalAmountDTO>(API_URL, '/suscription/renewal-amount', { params });
  }

  getRenewalOffers(suscriptionId: number): Observable<RenewalOfferDTO[]> {
    const params = new HttpParams().set('suscriptionId', String(suscriptionId));
    return this.api.get<RenewalOfferDTO[]>(API_URL, '/suscription/renewal-offers', { params });
  }

  createRenewalPayment(payload: RenewalPaymentRequestDTO): Observable<RenewalPaymentResponseDTO> {
    return this.api.post<RenewalPaymentResponseDTO>(API_URL, '/suscription/renewal-payment', payload);
  }

  public canReadModule(moduleName: AuthModules | string, formatModuleName = (o) => o) {
    let module = this.getAuthModule(moduleName, formatModuleName);
    if (module && module.read)
      return true;
    return false;
  }

  public canEditModule(moduleName: AuthModules | string, formatModuleName = (o) => o) {
    let module = this.getAuthModule(moduleName, formatModuleName);
    if (module && module.update)
      return true;
    return false;
  }

  public canCreateModule(moduleName: AuthModules | string, formatModuleName = (o) => o) {
    let module = this.getAuthModule(moduleName, formatModuleName);
    if (module && module.create)
      return true;
    return false;
  }

  public canDeleteModule(moduleName: AuthModules | string, formatModuleName = (o) => o) {
    let module = this.getAuthModule(moduleName, formatModuleName);
    if (module && module.delete)
      return true;
    return false;
  }

  public canAccessToDynamicConfiguration() {
    return this.canAccessToModule(AuthModules.CONFIG_HISTORIA_CLINICA) ||
      this.canAccessToModule(AuthModules.CONFIG_HISTORIA_ALIMENTARIA) ||
      this.canAccessToModule(AuthModules.CONFIG_HISTORIA_DEPORTIVA) ||
      this.canAccessToModule(AuthModules.CONFIG_OBJETIVOS) ||
      this.canAccessToModule(AuthModules.CONFIG_CONSULTA);
  }

  public canAccessToPatientHistory() {
    return this.canAccessToModule(AuthModules.PACIENTE_HISTORIA_CLINICA) ||
      this.canAccessToModule(AuthModules.PACIENTE_HISTORIA_ALIMENTARIA) ||
      this.canAccessToModule(AuthModules.PACIENTE_HISTORIA_DEPORTIVA) ||
      this.canAccessToModule(AuthModules.PACIENTE_OBJETIVOS) ||
      this.canAccessToModule(AuthModules.PACIENTE_CONSULTA);
  }

  public getAuthModule(moduleName: string, formatModuleName = (o) => o) {
    let actionModule;
    if (this.auth && this.auth.roleDefinitions && this.auth.roleDefinitions.length > 0) {
      let rol = this.auth.roleDefinitions[0];
      actionModule = rol.modules.find(m => formatModuleName(m.name) == moduleName);
    }
    return actionModule;
  }

  subscribeUser(userData: any): Observable<any> {
    const apiUrl = `${this.getUrl(API_URL)}/suscription`;
    const headers = this.getTimezoneHeaderPublic();


    return this.http.post(apiUrl, userData, { headers });
  }

  getStateSuscription(state: string): Observable<any> {
    const apiUrl = `${this.getUrl(API_URL)}/suscriptions`;
    const headers = this.getTimezoneHeader();
    return this.http.post(apiUrl, state, { headers });
  }

  verifyUser(accountName: string, email: string, ownerDni: string): Observable<any> {
    const apiUrl = `${this.getUrl(API_URL)}/suscription/pre-validation`;

    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    // Define headers for the request
    const login_response: LoginResponse = JSON.parse(sessionStorage.getItem('login_response'));
    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        'X-Timezone': timeZone, // Agregar el encabezado X-Timezone
      }),
      params: new HttpParams()
        .set('accountName', accountName)
        .set('email', email)
        .set('ownerDni', ownerDni)
    };

    return this.http.get(apiUrl, httpOptions);
  }

}

export enum AuthModules {
  REPORTE_ANTROPROMETRICO = "reporte.antropometrico_basico",
  REPORTE_COMPARATIVO = "reporte.comparativo",
  REPORTE_COMPARATIVO_VS_REFERENCIAL = "reporte.comparativo_vs_referencial",
  REPORTE_SCREENING = "reporte.screening",
  REPORTE_CRONOGRAMA = "reporte.cronograma",
  REPORTE_GRUPAL = "reporte.grupal.fa5c",
  REPORTE_OBJETIVO = "reporte.objetivo",

  PACIENTE = "paciente",
  PACIENTE_EVALUACIONES = "paciente.evaluaciones",
  PACIENTE_HISTORIA_CLINICA = "paciente.historia_clinica",
  PACIENTE_HISTORIA_ALIMENTARIA = "paciente.historia_alimentaria",
  PACIENTE_HISTORIA_DEPORTIVA = "paciente.historia_deportiva",
  PACIENTE_OBJETIVOS = "paciente.objetivos_composicion_corporal",
  PACIENTE_CONSULTA = "paciente.consulta_medica",
  PACIENTE_ARCHIVOS = "files.attach",
  PACIENTE_PLANES = "eating.plans",
  PACIENTE_DOCUMENTOS_APP = "documents.app",
  PACIENTE_PERSONAL_DATA = "personal.data",
  PACIENTE_OBJETIVO = "paciente.objetivo",

  CONFIG_HISTORIA_CLINICA = "config.historia_clinica",
  CONFIG_HISTORIA_ALIMENTARIA = "config.historia_alimentaria",
  CONFIG_HISTORIA_DEPORTIVA = "config.historia_deportiva",
  CONFIG_OBJETIVOS = "config.objetivos_composicion_corporal",
  CONFIG_CONSULTA = "config.consulta_medica",
  CONFIG_REFERENCIA = "config.referencia",
  CONFIG_SEGMENTOS = "config.segmentos",
  CONFIG_COMIDAS = "config.comidas",
  CONFIG_LOGOS = "config.logos",
  CONFIG_STAFF = "config.staff",

  BACKOFFICE_SUSCRIPTION = "backoffice.suscription",
  INFO_GRUPAL_DINAMICA = "info.grupal.dinamica",
}
